import { FirebaseError } from "firebase/app";
import { serverTimestamp, setDoc } from "firebase/firestore";
import { paths } from "@/data/firestore/paths";
import type { ErrorRecord } from "@/domain/types";

export type AppErrorCode = "access" | "connectivity" | "missing-tenant" | "missing-document" | "invalid-data" | "write-failed" | "subscription-failed" | "conflict" | "lock-lost" | "validation" | "ai-failed" | "onboarding-failed" | "unavailable" | "unexpected";

export class AppError extends Error {
  constructor(public readonly code: AppErrorCode, message: string, public readonly retryable = false, options?: ErrorOptions) {
    super(message, options);
    this.name = "AppError";
  }
}

export function toAppError(error: unknown, fallback: AppErrorCode = "unexpected"): AppError {
  if (error instanceof AppError) return error;
  if (error instanceof FirebaseError) {
    const code = error.code.replace(/^firestore\//, "").replace(/^auth\//, "").replace(/^functions\//, "");
    if (code === "permission-denied" || code === "unauthorized" || code === "unauthenticated") return new AppError("access", "You do not have access to this information.", false, { cause: error });
    if (code === "not-found") return new AppError("missing-document", "The requested information is unavailable.", false, { cause: error });
    if (code === "unavailable" || code === "network-request-failed" || code === "deadline-exceeded") return new AppError("connectivity", "We could not connect. Check your connection and try again.", true, { cause: error });
    if (code === "failed-precondition" || code === "resource-exhausted") return new AppError("unavailable", "This service is temporarily unavailable. Please try again.", true, { cause: error });
    if (code === "internal" || code === "unknown") return new AppError(fallback, "We couldn't complete that request. Please try again.", true, { cause: error });
  }
  return new AppError(fallback, error instanceof Error ? error.message : "Something went wrong.", fallback !== "invalid-data" && fallback !== "validation", { cause: error });
}

type LogErrorInput = {
  customerId?: string | null | undefined;
  operation: string;
  error: unknown;
  category?: ErrorRecord["category"];
  source?: ErrorRecord["source"];
  entity?: ErrorRecord["entity"] | undefined;
  tool?: string | undefined;
  details?: Record<string, unknown> | undefined;
};

function sanitized(details?: Record<string, unknown>): Record<string, unknown> | undefined {
  if (!details) return undefined;
  const blocked = /token|secret|password|authorization|api.?key/i;
  return Object.fromEntries(Object.entries(details).filter(([key]) => !blocked.test(key)).map(([key, value]) => [key, typeof value === "string" ? value.slice(0, 500) : value]));
}

export async function logTechnicalError(input: LogErrorInput): Promise<void> {
  const appError = toAppError(input.error);
  const correlationId = crypto.randomUUID();
  console.error(`[Qotta ${input.operation}] ${appError.code} (${correlationId})`, appError.cause ?? appError);
  if (!input.customerId) return;
  try {
    await setDoc(paths.error(input.customerId, correlationId), {
      code: appError.code,
      category: input.category ?? "unexpected",
      operation: input.operation,
      message: appError.message.slice(0, 1000),
      severity: "error",
      retryable: appError.retryable,
      source: input.source ?? "client",
      ...(input.entity ? { entity: input.entity } : {}),
      ...(input.tool ? { tool: input.tool } : {}),
      correlationId,
      ...(sanitized(input.details) ? { sanitizedDetails: sanitized(input.details) } : {}),
      ...(appError.stack ? { stack: appError.stack.slice(0, 4000) } : {}),
      createdAt: serverTimestamp(),
    });
  } catch (loggingError) {
    console.error("[Qotta error logger] Failed to persist technical error", loggingError);
  }
}

export function reportDataError(context: string, error: AppError, customerId?: string | null): void {
  void logTechnicalError({ customerId, operation: context, error, category: "data", source: "firebase" });
}
